import { distanceMeters, type LngLat } from '../../maps/mapboxDrivingRoute';

export type TurnInstruction =
  | 'Turn Left'
  | 'Turn Right'
  | 'Slight Left'
  | 'Slight Right'
  | 'Continue Straight'
  | 'Arrived';

export type NextInstructionResult = {
  instruction: TurnInstruction;
  distanceMeters: number;
  targetBearing: number;
};

const STRAIGHT_THRESHOLD_DEG = 15;
const SLIGHT_THRESHOLD_DEG = 45;
const ARRIVAL_THRESHOLD_METERS = 8;

const toRad = (d: number) => (d * Math.PI) / 180;
const toDeg = (r: number) => (r * 180) / Math.PI;

/** Great-circle compass bearing from `a` to `b`, degrees clockwise from north. */
export function bearingBetween(a: LngLat, b: LngLat): number {
  const lon1 = toRad(a[0]);
  const lat1 = toRad(a[1]);
  const lon2 = toRad(b[0]);
  const lat2 = toRad(b[1]);
  const dLon = lon2 - lon1;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/** Signed smallest angle to turn from `from` to `to`, in [-180, 180]. Positive = clockwise/right. */
export function angleDelta(from: number, to: number): number {
  let delta = (to - from) % 360;
  if (delta > 180) delta -= 360;
  if (delta < -180) delta += 360;
  return delta;
}

export function instructionForDelta(delta: number): TurnInstruction {
  const abs = Math.abs(delta);
  if (abs <= STRAIGHT_THRESHOLD_DEG) return 'Continue Straight';
  if (abs <= SLIGHT_THRESHOLD_DEG) return delta > 0 ? 'Slight Right' : 'Slight Left';
  return delta > 0 ? 'Turn Right' : 'Turn Left';
}

/** Index of the route point closest to `position` — a basic corridor-snap.
 *
 * When `previousIndex` is given, the search is limited to a small window
 * around it instead of the entire route. A pure global-nearest search lets
 * the match jump to a much later point whenever the route happens to pass
 * close to itself — a same-room visibility shortcut cutting back near an
 * earlier leg, two floors' corridors sitting near the same lng/lat, GPS/
 * simulator jitter nudging a position slightly closer to a later point than
 * the current one. That reads as the remaining route line suddenly
 * truncating or vanishing, or the turn instruction jumping — confirmed as
 * the actual cause of exactly that report, not a rendering bug. Bounding
 * the search keeps progress local and effectively monotonic, matching how
 * a person actually walks a route instead of "nearest point anywhere on
 * the polyline right now". */
export function nearestRouteIndex(
  position: LngLat,
  routePoints: LngLat[],
  previousIndex?: number,
  forwardWindow = 6,
): number {
  const searchStart = previousIndex != null ? Math.max(0, previousIndex - 1) : 0;
  const searchEnd =
    previousIndex != null ? Math.min(routePoints.length - 1, previousIndex + forwardWindow) : routePoints.length - 1;

  let bestIndex = searchStart;
  let bestDist = Infinity;
  for (let index = searchStart; index <= searchEnd; index++) {
    const dist = distanceMeters(position, routePoints[index]);
    if (dist < bestDist) {
      bestDist = dist;
      bestIndex = index;
    }
  }
  return bestIndex;
}

/**
 * "Current heading" derived purely from route geometry — the bearing of the
 * segment leading into `index` — rather than from raw position-to-position
 * deltas. Real/simulated position ticks are noisy (GPS jitter, the
 * simulator's wander wobble); that noise is often larger than a single
 * tick's actual forward progress, which makes a heading built from
 * consecutive positions swing wildly. The route itself doesn't jitter, so
 * this stays stable while walking a segment and only changes once the
 * walker's nearest index actually advances past a corner.
 */
export function routeHeadingAtIndex(routePoints: LngLat[], index: number): number {
  if (routePoints.length < 2) return 0;
  const clampedIndex = Math.max(0, Math.min(index, routePoints.length - 1));
  if (clampedIndex === 0) {
    return bearingBetween(routePoints[0], routePoints[1]);
  }
  return bearingBetween(routePoints[clampedIndex - 1], routePoints[clampedIndex]);
}

const METERS_PER_DEG_LAT = 111_320;

/**
 * Builds a 3-point route (start → corner → end) with exactly one real turn,
 * using flat-earth meters math (accurate enough for the few-hundred-metre
 * distances an indoor walk covers) instead of naive lng/lat fraction
 * interpolation. Fraction-based interpolation degenerates badly whenever
 * start/end share a longitude or latitude (a very common case here, since
 * the simulated walk start is just an offset due north/south of pickup) —
 * every "waypoint" collapses onto the same point, producing zero-length
 * segments whose bearing is meaningless and swings wildly against the real
 * segments around them. Placing the corner via a local forward/right frame
 * (relative to the real start→end bearing) avoids that regardless of the
 * actual start/end geometry, and always produces a genuine, non-degenerate
 * turn.
 */
export function buildTurnRoute(
  start: LngLat,
  end: LngLat,
  straightFraction: number,
  lateralMeters: number,
): LngLat[] {
  const [startLng, startLat] = start;
  const [endLng, endLat] = end;
  const metersPerDegLng = METERS_PER_DEG_LAT * Math.cos(toRad(startLat));
  const dx = (endLng - startLng) * metersPerDegLng;
  const dy = (endLat - startLat) * METERS_PER_DEG_LAT;
  const length = Math.sqrt(dx * dx + dy * dy);
  if (length < 1 || metersPerDegLng === 0) return [start, end];

  const fwdX = dx / length;
  const fwdY = dy / length;
  const rightX = fwdY;
  const rightY = -fwdX;
  const straightM = straightFraction * length;
  const cornerX = fwdX * straightM + rightX * lateralMeters;
  const cornerY = fwdY * straightM + rightY * lateralMeters;
  const corner: LngLat = [
    startLng + cornerX / metersPerDegLng,
    startLat + cornerY / METERS_PER_DEG_LAT,
  ];
  return [start, corner, end];
}

/**
 * Upcoming turn instruction from the walker's actual position toward the
 * next route waypoint, relative to their current heading. Returns null when
 * there isn't enough route to navigate against.
 */
export function nextInstruction(
  position: LngLat,
  routePoints: LngLat[],
  currentIndex: number,
  currentHeading: number,
): NextInstructionResult | null {
  if (routePoints.length < 2) return null;

  const destination = routePoints[routePoints.length - 1];
  if (distanceMeters(position, destination) <= ARRIVAL_THRESHOLD_METERS) {
    return { instruction: 'Arrived', distanceMeters: 0, targetBearing: currentHeading };
  }

  const nextIndex = Math.min(currentIndex + 1, routePoints.length - 1);
  const targetPoint = routePoints[nextIndex];
  const targetBearing = bearingBetween(position, targetPoint);
  const delta = angleDelta(currentHeading, targetBearing);

  return {
    instruction: instructionForDelta(delta),
    distanceMeters: distanceMeters(position, targetPoint),
    targetBearing,
  };
}
